import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import * as React from "react";
import styled from "styled-components";

const enum fields {
    eyebrow = "eyebrow",
    headingLine1 = "headingLine1",
    headingLine2 = "headingLine2",
    headingLine3 = "headingLine3",
    bodyLine1 = "bodyLine1",
    bodyLine2 = "bodyLine2",
    bodyLine3 = "bodyLine3",
    image1 = "image1",
    image2 = "image2",
    image1Overlay = "image1Overlay",
    image2Overlay = "image2Overlay",
    foundedLabel = "foundedLabel",
    foundedYear = "foundedYear",
    foundedLocation = "foundedLocation",
    ownershipLabel = "ownershipLabel",
    ownershipValue = "ownershipValue",
    ownershipUnit = "ownershipUnit",
    ownershipDescription = "ownershipDescription",
    backgroundColor = "backgroundColor",
    accentColor = "accentColor",
    foundedBackgroundColor = "foundedBackgroundColor",
    foundedTextColor = "foundedTextColor",
    textColor = "textColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.eyebrow]: string;
        [fields.headingLine1]: string;
        [fields.headingLine2]: string;
        [fields.headingLine3]: string;
        [fields.bodyLine1]: string;
        [fields.bodyLine2]: string;
        [fields.bodyLine3]: string;
        [fields.image1]: string;
        [fields.image2]: string;
        [fields.image1Overlay]: string;
        [fields.image2Overlay]: string;
        [fields.foundedLabel]: string;
        [fields.foundedYear]: string;
        [fields.foundedLocation]: string;
        [fields.ownershipLabel]: string;
        [fields.ownershipValue]: string;
        [fields.ownershipUnit]: string;
        [fields.ownershipDescription]: string;
        [fields.backgroundColor]: string;
        [fields.accentColor]: string;
        [fields.foundedBackgroundColor]: string;
        [fields.foundedTextColor]: string;
        [fields.textColor]: string;
    };
}

type Props = OwnProps;

const CompanyHistoryFeature: React.FC<Props> = ({ fields: content }) => {
    return (
        <Layout backgroundColor={content.backgroundColor} textColor={content.textColor}>
            <StoryPanel>
                <Eyebrow accentColor={content.accentColor}>{content.eyebrow}</Eyebrow>
                <Heading>
                    <span>{content.headingLine1}</span>
                    <AccentHeading accentColor={content.accentColor}>{content.headingLine2}</AccentHeading>
                    <AccentHeading accentColor={content.accentColor}>{content.headingLine3}</AccentHeading>
                </Heading>
                <BodyCopy>
                    <span>{content.bodyLine1}</span>
                    <span>{content.bodyLine2}</span>
                    <span>{content.bodyLine3}</span>
                </BodyCopy>
            </StoryPanel>
            <DetailsPanel>
                <ImageRow>
                        <ImageWithOverlay>
                            <FeatureImage src={content.image1} alt="" />
                            <ImageOverlay>{content.image1Overlay}</ImageOverlay>
                        </ImageWithOverlay>
                        <ImageWithOverlay>
                            <FeatureImage src={content.image2} alt="" />
                            <ImageOverlay>{content.image2Overlay}</ImageOverlay>
                        </ImageWithOverlay>
                </ImageRow>
                <StatsRow>
                    <StatCard backgroundColor={content.foundedBackgroundColor} textColor={content.foundedTextColor}>
                        <StatLabel>{content.foundedLabel}</StatLabel>
                        <StatValue>{content.foundedYear}</StatValue>
                        <StatDescription>{content.foundedLocation}</StatDescription>
                    </StatCard>
                    <StatCard backgroundColor={content.accentColor} textColor={content.textColor}>
                        <StatLabel>{content.ownershipLabel}</StatLabel>
                        <StatValue>
                            {content.ownershipValue}
                            <StatUnit>{content.ownershipUnit}</StatUnit>
                        </StatValue>
                        <StatDescription>{content.ownershipDescription}</StatDescription>
                    </StatCard>
                </StatsRow>
            </DetailsPanel>
        </Layout>
    );
};

const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [
        { name: fields.eyebrow, displayName: "Eyebrow", editorTemplate: "TextField", defaultValue: "EST. 1937  •  Philadelphia", fieldType: "General" },
        { name: fields.headingLine1, displayName: "Heading Line 1", editorTemplate: "TextField", defaultValue: "EIGHTY-FIVE YEARS", fieldType: "General" },
        { name: fields.headingLine2, displayName: "Heading Line 2", editorTemplate: "TextField", defaultValue: "OF PIPE, VALVES AND", fieldType: "General" },
        { name: fields.headingLine3, displayName: "Heading Line 3", editorTemplate: "TextField", defaultValue: "FOLLOW-THROUGH", fieldType: "General" },
        { name: fields.bodyLine1, displayName: "Body Line 1", editorTemplate: "TextField", defaultValue: "Cooney Brothers is a third-generation, family-operated PVF distributor.", fieldType: "General" },
        { name: fields.bodyLine2, displayName: "Body Line 2", editorTemplate: "TextField", defaultValue: "The same family. The same city.", fieldType: "General" },
        { name: fields.bodyLine3, displayName: "Body Line 3", editorTemplate: "TextField", defaultValue: "Three generations of customers who know our reps by first name.", fieldType: "General" },
        { name: fields.backgroundColor, displayName: "Story Background", editorTemplate: "ColorPickerField", defaultValue: "#000000", fieldType: "General" },
        { name: fields.accentColor, displayName: "Accent Color", editorTemplate: "ColorPickerField", defaultValue: "#ed1c24", fieldType: "General" },
        { name: fields.foundedBackgroundColor, displayName: "Founded Card Background", editorTemplate: "ColorPickerField", defaultValue: "#e4dada", fieldType: "General" },
        { name: fields.foundedTextColor, displayName: "Founded Card Text", editorTemplate: "ColorPickerField", defaultValue: "#424242", fieldType: "General" },
        { name: fields.textColor, displayName: "Text Color", editorTemplate: "ColorPickerField", defaultValue: "#ffffff", fieldType: "General" },
        { name: fields.image1, displayName: "Top Image 1", editorTemplate: "ImagePickerField", defaultValue: "", fieldType: "General" },
        { name: fields.image2, displayName: "Top Image 2", editorTemplate: "ImagePickerField", defaultValue: "", fieldType: "General" },
        { name: fields.image1Overlay, displayName: "Top Image 1 Overlay", editorTemplate: "TextField", defaultValue: "1937", fieldType: "General" },
        { name: fields.image2Overlay, displayName: "Top Image 2 Overlay", editorTemplate: "TextField", defaultValue: "TODAY", fieldType: "General" },
        { name: fields.foundedLabel, displayName: "Founded Label", editorTemplate: "TextField", defaultValue: "FOUNDED", fieldType: "General" },
        { name: fields.foundedYear, displayName: "Founded Year", editorTemplate: "TextField", defaultValue: "1937", fieldType: "General" },
        { name: fields.foundedLocation, displayName: "Founded Location", editorTemplate: "TextField", defaultValue: "Philadelphia, PA", fieldType: "General" },
        { name: fields.ownershipLabel, displayName: "Ownership Label", editorTemplate: "TextField", defaultValue: "OWNERSHIP", fieldType: "General" },
        { name: fields.ownershipValue, displayName: "Ownership Value", editorTemplate: "TextField", defaultValue: "3", fieldType: "General" },
        { name: fields.ownershipUnit, displayName: "Ownership Unit", editorTemplate: "TextField", defaultValue: "RD", fieldType: "General" },
        { name: fields.ownershipDescription, displayName: "Ownership Description", editorTemplate: "TextField", defaultValue: "generation, family-operated", fieldType: "General" }
        ],
};

const widgetModule: WidgetModule = {
    component: CompanyHistoryFeature,
    definition,
};

export default widgetModule;

const Layout = styled.div<{ backgroundColor: string; textColor: string }>`
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(300px, 1fr);
    gap: 10px;
    background: ${({ backgroundColor }) => backgroundColor || "#000"};
    color: ${({ textColor }) => textColor || "#fff"};
    padding: 0;
    font-family: Arial, sans-serif;
    @media (max-width: 800px) {
        grid-template-columns: 1fr;
    }
`;

const StoryPanel = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 480px;
    min-width: 0;
    padding: 56px 7%;
    background: #000000;
    @media (max-width: 800px) {
        min-height: 0;
        padding: 44px 7%;
    }
`;

const Eyebrow = styled.div<{ accentColor: string }>`
    margin-bottom: 48px;
    font-size: 17px;
    font-weight: 700;
    letter-spacing: 0.2px;
    &::before {
        content: "";
        display: inline-block;
        width: 7px;
        height: 7px;
        margin: 0 12px 3px 0;
        border-radius: 50%;
        background: ${({ accentColor }) => accentColor || "#ed1c24"};
    }
`;

const Heading = styled.h2`
    margin: 0;
    font-size: clamp(34px, 3.5vw, 72px);
    font-weight: 800;
    line-height: 1.08;
    letter-spacing: 0;
    text-transform: uppercase;
    overflow-wrap: anywhere;
    span,
    > span {
        display: block;
    }
`;

const AccentHeading = styled.span<{ accentColor: string }>`
    color: ${({ accentColor }) => accentColor || "#ed1c24"};
`;

const BodyCopy = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 48px;
    font-size: 17px;
    font-weight: 600;
    line-height: 1.3;
    overflow-wrap: anywhere;
`;

const DetailsPanel = styled.div`
    display: grid;
    grid-template-rows: auto 1fr;
    gap: 8px;
    padding-right: 0px;
    min-width: 0;
    @media (max-width: 800px) {
        padding: 0px;
    }
`;

const StatsRow = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: repeat(2, minmax(0, 1fr));
    gap: 8px;

    @media (max-width: 800px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        grid-template-rows: minmax(165px, auto);
    }
`;

const ImageRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
`;

const ImageWithOverlay = styled.div`
    position: relative;
    min-width: 0;
`;

const FeatureImage = styled.img`
    display: block;
    width: 100%;
    aspect-ratio: 1.3;
    object-fit: cover;
`;

const ImageOverlay = styled.div`
    position: absolute;
    right: 16px;
    bottom: 14px;
    max-width: calc(100% - 32px);
    color: #fff;
    font-size: clamp(16px, 2vw, 30px);
    font-weight: 800;
    line-height: 1;
    overflow-wrap: anywhere;
    text-align: right;
    text-shadow: 0 1px 5px rgba(0, 0, 0, 0.8);
`;

const StatCard = styled.div<{ backgroundColor: string; textColor: string }>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 165px;
    padding: 22px 32px;
    background: ${({ backgroundColor }) => backgroundColor || "#e4dada"};
    color: ${({ textColor }) => textColor || "#fff"};

    @media (max-width: 480px) {
        min-width: 0;
        padding: 16px clamp(12px, 4vw, 20px);
    }
`;

const StatLabel = styled.div`
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: 700;
`;

const StatValue = styled.div`
    font-size: 50px;
    font-weight: 800;
    line-height: 0.95;

    @media (max-width: 480px) {
        font-size: clamp(32px, 10vw, 44px);
    }
`;

const StatUnit = styled.span`
    margin-left: 2px;
    font-size: 32px;
    text-transform: uppercase;
    vertical-align: super;

    @media (max-width: 480px) {
        font-size: clamp(18px, 6vw, 26px);
    }
`;

const StatDescription = styled.div`
    margin-top: 16px;
    font-size: 16px;
    font-weight: 600;
`;
